/**
 * API Services para Configurações de Chamadas de Entrada
 * VigosCloud - Incoming Call System
 * 
 * Baseado no padrão dos services existentes (outbound calls)
 * Todas as chamadas incluem autenticação via token
 */

import axios from 'axios';
import accessLocalStorage from './accessLocalStorage';

/**
 * Cria uma nova configuração de chamada de entrada
 * @param {Object} configData - Dados da configuração
 * @returns {Promise} Response da API
 */
export const createIncomingConfig = async (configData) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log('🔍 [INCOMING-API] Criando configuração:', configData);
    
    const response = await axios.post(
      `${ipRequest}api/config/incoming/configs`,
      configData,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [INCOMING-API] Configuração criada:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao criar configuração:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao criar configuração',
      message: error.response?.data?.message || error.message,
      details: error.response?.data?.details || null
    };
  }
};

/**
 * Lista todas as configurações de chamadas de entrada
 * @param {Object} filters - Filtros de busca
 * @returns {Promise} Response da API
 */
export const getAllIncomingConfigs = async (filters = {}) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log('🔍 [INCOMING-API] Listando configurações com filtros:', filters);
    
    // Construir URL com parâmetros
    const url = new URL(`${ipRequest}api/config/incoming/configs`);
    
    if (filters.tenant) url.searchParams.append('tenant', filters.tenant);
    if (filters.destinationType) url.searchParams.append('destination_type', filters.destinationType);
    if (filters.activeOnly !== undefined) url.searchParams.append('active_only', filters.activeOnly);
    if (filters.hasTrunk !== undefined) url.searchParams.append('has_trunk', filters.hasTrunk);
    if (filters.limit) url.searchParams.append('limit', filters.limit);
    if (filters.offset) url.searchParams.append('offset', filters.offset);
    
    const response = await axios.get(url.toString(), {
      headers: { Authorization: token }
    });
    
    console.log(`✅ [INCOMING-API] Encontradas ${response.data.data?.length || 0} configurações`);
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao listar configurações:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao listar configurações',
      data: []
    };
  }
};

/**
 * Busca configuração de chamada de entrada por ID
 * @param {number} id - ID da configuração
 * @returns {Promise} Response da API
 */
export const getIncomingConfigById = async (id) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [INCOMING-API] Buscando configuração ID: ${id}`);
    
    const response = await axios.get(
      `${ipRequest}api/config/incoming/configs/${id}`,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [INCOMING-API] Configuração encontrada');
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao buscar configuração:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar configuração',
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * Atualiza configuração de chamada de entrada
 * @param {number} id - ID da configuração
 * @param {Object} updateData - Dados para atualização
 * @returns {Promise} Response da API
 */
export const updateIncomingConfig = async (id, updateData) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [INCOMING-API] Atualizando configuração ID: ${id}`, updateData);
    
    const response = await axios.put(
      `${ipRequest}api/config/incoming/configs/${id}`,
      updateData,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [INCOMING-API] Configuração atualizada');
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao atualizar configuração:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao atualizar configuração',
      message: error.response?.data?.message || error.message,
      details: error.response?.data?.details || null
    };
  }
};

/**
 * Remove configuração de chamada de entrada
 * @param {number} id - ID da configuração
 * @returns {Promise} Response da API
 */
export const deleteIncomingConfig = async (id) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [INCOMING-API] Deletando configuração ID: ${id}`);
    
    const response = await axios.delete(
      `${ipRequest}api/config/incoming/configs/${id}`,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [INCOMING-API] Configuração deletada');
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao deletar configuração:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao deletar configuração',
      message: error.response?.data?.message || error.message
    };
  }
};

/**
 * FUNCIONALIDADE ESPECÍFICA: Lista troncos disponíveis para um tenant (entrada + bi-direcionais)
 * Implementa regra: ps_endpoint_id_ips com accountcode = tenant e IDs não-numéricos
 * @param {string|Object} tenantParam - Tenant como string ou objeto {tenant: "valor"}
 * @returns {Promise} Response da API
 */
export const getAvailableTrunks = async (tenantParam = null) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    // Normalizar o parâmetro tenant (pode vir como string ou objeto)
    const tenant = typeof tenantParam === 'object' && tenantParam?.tenant 
      ? tenantParam.tenant 
      : tenantParam;
      
    console.log('🔍 [INCOMING-API] Buscando troncos disponíveis para tenant:', tenant);
    console.log('🔍 [INCOMING-API] Parâmetro original:', tenantParam, 'Normalizado:', tenant);
    
    const url = new URL(`${ipRequest}api/config/incoming/trunks`);
    if (tenant) url.searchParams.append('tenant', tenant);
    
    const response = await axios.get(url.toString(), {
      headers: { Authorization: token }
    });
    
    console.log(`✅ [INCOMING-API] Encontrados ${response.data.data?.length || 0} troncos`);
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao buscar troncos:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar troncos',
      data: []
    };
  }
};

/**
 * FUNCIONALIDADE ESPECÍFICA: Lista troncos de saída para chamadas externas (apenas do tenant)
 * Filtra apenas troncos com accountcode = tenant para chamadas externas
 * @param {string|Object} tenantParam - Tenant como string ou objeto {tenant: "valor"}
 * @returns {Promise} Response da API
 */
export const getAvailableOutboundTrunks = async (tenantParam = null) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    // Normalizar o parâmetro tenant (pode vir como string ou objeto)
    const tenant = typeof tenantParam === 'object' && tenantParam?.tenant 
      ? tenantParam.tenant 
      : tenantParam;
      
    console.log('🔍 [INCOMING-API] Buscando troncos de saída para tenant:', tenant);
    console.log('🔍 [INCOMING-API] Parâmetro original:', tenantParam, 'Normalizado:', tenant);
    
    const url = new URL(`${ipRequest}api/config/incoming/outbound-trunks`);
    if (tenant) url.searchParams.append('tenant', tenant);
    
    const response = await axios.get(url.toString(), {
      headers: { Authorization: token }
    });
    
    console.log(`✅ [INCOMING-API] Encontrados ${response.data.data?.length || 0} troncos de saída`);
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao buscar troncos de saída:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar troncos de saída',
      data: []
    };
  }
};

/**
 * Busca estatísticas de configurações por tenant
 * @param {string} tenant - Tenant (opcional para root)
 * @returns {Promise} Response da API
 */
export const getIncomingStatistics = async (tenant = null) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [INCOMING-API] Buscando estatísticas${tenant ? ` para tenant: ${tenant}` : ''}`);
    
    const url = new URL(`${ipRequest}api/config/incoming/statistics`);
    if (tenant) url.searchParams.append('tenant', tenant);
    
    const response = await axios.get(url.toString(), {
      headers: { Authorization: token }
    });
    
    console.log('✅ [INCOMING-API] Estatísticas obtidas');
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao buscar estatísticas:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar estatísticas',
      data: {}
    };
  }
};

/**
 * Busca dados completos por tenant (configurações + estatísticas)
 * Endpoint otimizado para o frontend carregar dados completos
 * @param {string} tenant - Tenant (opcional para root)
 * @returns {Promise} Response da API
 */
export const getIncomingDataByTenant = async (tenant = null) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [INCOMING-API] Buscando dados completos${tenant ? ` para tenant: ${tenant}` : ''}`);
    
    const url = new URL(`${ipRequest}api/config/incoming/data`);
    if (tenant) url.searchParams.append('tenant', tenant);
    
    const response = await axios.get(url.toString(), {
      headers: { Authorization: token }
    });
    
    console.log('✅ [INCOMING-API] Dados completos obtidos');
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao buscar dados por tenant:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar dados',
      data: {}
    };
  }
};

/**
 * Busca contextos DISA disponíveis para um tenant
 * @param {string} tenant - Tenant obrigatório
 * @returns {Promise} Response da API
 */
export const getAvailableContexts = async (tenant) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    if (!tenant) {
      throw new Error('Tenant é obrigatório para buscar contextos');
    }
    
    console.log('🔍 [INCOMING-API] Buscando contextos DISA para tenant:', tenant);
    
    const url = new URL(`${ipRequest}api/config/incoming/contexts`);
    url.searchParams.append('tenant', tenant);
    
    const response = await axios.get(url.toString(), {
      headers: { Authorization: token }
    });
    
    console.log(`✅ [INCOMING-API] Encontrados ${response.data.data?.length || 0} contextos`);
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao buscar contextos:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar contextos',
      data: []
    };
  }
};

// ============================================================================
// FUNÇÕES DE DESENVOLVIMENTO E DEBUG
// ============================================================================

/**
 * Visualiza o dialplan gerado para uma configuração (apenas desenvolvimento)
 * @param {number} id - ID da configuração
 * @returns {Promise} Response da API
 */
export const getDialplanDebug = async (id) => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log(`🔍 [INCOMING-API] Buscando dialplan debug para ID: ${id}`);
    
    const response = await axios.get(
      `${ipRequest}api/config/incoming/debug/dialplan/${id}`,
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [INCOMING-API] Dialplan debug obtido');
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao buscar dialplan debug:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao buscar dialplan debug',
      data: null
    };
  }
};

/**
 * Força reload do dialplan do Asterisk (apenas desenvolvimento)
 * @returns {Promise} Response da API
 */
export const forceDialplanReload = async () => {
  const { token, ipRequest } = await accessLocalStorage.getUserLocalStorage();
  
  try {
    console.log('🔍 [INCOMING-API] Forçando reload do dialplan');
    
    const response = await axios.post(
      `${ipRequest}api/config/incoming/debug/reload`,
      {},
      { headers: { Authorization: token } }
    );
    
    console.log('✅ [INCOMING-API] Reload executado');
    return response.data;
  } catch (error) {
    console.error('❌ [INCOMING-API] Erro ao executar reload:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Erro ao executar reload',
      message: error.response?.data?.message || error.message
    };
  }
};

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Valida dados de configuração antes de enviar para API
 * @param {Object} configData - Dados da configuração
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateIncomingConfig = (configData) => {
  const errors = [];
  
  // Validações obrigatórias
  if (!configData.tenant || configData.tenant.trim().length === 0) {
    errors.push('Tenant é obrigatório');
  }
  
  if (!configData.mask || configData.mask.trim().length === 0) {
    errors.push('Máscara DID é obrigatória');
  }
  
  if (!configData.destination_type_main) {
    errors.push('Tipo de destino principal é obrigatório');
  }
  
  // Validações condicionais
  if (configData.destination_type_main && 
      !['endpoint', 'hangup'].includes(configData.destination_type_main) && 
      (!configData.destination_main || configData.destination_main.trim().length === 0)) {
    errors.push('Destino principal é obrigatório para este tipo');
  }
  
  if (configData.audio_enabled) {
    if (!configData.audio_file) errors.push('Arquivo de áudio é obrigatório quando áudio está habilitado');
    if (!configData.audio_type) errors.push('Tipo de áudio é obrigatório quando áudio está habilitado');
    if (!configData.audio_accountcode) errors.push('AccountCode do áudio é obrigatório quando áudio está habilitado');
  }
  
  // Validações de overflows
  if (configData.overflows && Array.isArray(configData.overflows)) {
    configData.overflows.forEach((overflow, index) => {
      if (!overflow.destination_overflow || overflow.destination_overflow.trim().length === 0) {
        errors.push(`Destino do overflow ${index + 1} é obrigatório`);
      }
      if (!overflow.destination_type) {
        errors.push(`Tipo de destino do overflow ${index + 1} é obrigatório`);
      }
      if (overflow.timeout_seconds && (overflow.timeout_seconds < 1 || overflow.timeout_seconds > 300)) {
        errors.push(`Timeout do overflow ${index + 1} deve estar entre 1 e 300 segundos`);
      }
    });
  }
  
  // Validações de time-conditions
  if (configData.gotoiftime_rules && Array.isArray(configData.gotoiftime_rules)) {
    configData.gotoiftime_rules.forEach((rule, index) => {
      if (!rule.time_range) errors.push(`Faixa de horário da regra ${index + 1} é obrigatória`);
      if (!rule.days_of_week) errors.push(`Dias da semana da regra ${index + 1} são obrigatórios`);
      if (!rule.days_of_month) errors.push(`Dias do mês da regra ${index + 1} são obrigatórios`);
      if (!rule.months) errors.push(`Meses da regra ${index + 1} são obrigatórios`);
      if (!rule.destination) errors.push(`Destino da regra ${index + 1} é obrigatório`);
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Formata dados de configuração para envio à API
 * @param {Object} formData - Dados do formulário
 * @returns {Object} Dados formatados
 */
export const formatConfigDataForAPI = (formData) => {
  const formatted = {
    ...formData,
    // Garantir que valores booleanos sejam enviados corretamente
    recording_enabled: Boolean(formData.recording_enabled),
    audio_enabled: Boolean(formData.audio_enabled),
    hangup_after_audio: Boolean(formData.hangup_after_audio),
    active: formData.active !== false,
    
    // Limpar campos vazios
    trunk_name: formData.trunk_name?.trim() || null,
    destination_main: formData.destination_main?.trim() || null,
    description: formData.description?.trim() || null,
    
    // Garantir que arrays existam
    overflows: Array.isArray(formData.overflows) ? formData.overflows : [],
    gotoiftime_rules: Array.isArray(formData.gotoiftime_rules) ? formData.gotoiftime_rules : []
  };
  
  // Remover campos undefined/null desnecessários
  Object.keys(formatted).forEach(key => {
    if (formatted[key] === undefined || formatted[key] === '') {
      delete formatted[key];
    }
  });
  
  return formatted;
};

export default {
  createIncomingConfig,
  getAllIncomingConfigs,
  getIncomingConfigById,
  updateIncomingConfig,
  deleteIncomingConfig,
  getAvailableTrunks,
  getAvailableOutboundTrunks,
  getIncomingStatistics,
  getIncomingDataByTenant,
  getAvailableContexts,
  getDialplanDebug,
  forceDialplanReload,
  validateIncomingConfig,
  formatConfigDataForAPI
};
